import { useEffect, useRef, useState } from "react";
import { Button, Modal, Spinner } from "react-bootstrap";
import Swal from "sweetalert2";
import adminApi from "../../../../api/adminApi";
import adminUserCreditApi from "../../../../api/adminUserCreditApi";
import paymentApi from "../../../../api/paymentApi";

const QRCodeModal = ({
  show,
  handleClose,
  qrImage,
  credits,
  amount,
  transactionId,
  user,
  companyName,
  mobileNumber,
  collaborationName,
  location,
  country,
  managedBy,
  franchise,
  requestedFrom,
  requestedTo,
  price,
}) => {
  const [timeLeft, setTimeLeft] = useState(60); // 10 minutes in seconds
  const [isScanned, setIsScanned] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  const [creditPiceUnit, setCreditPiceUnit] = useState(0);
  const timerRef = useRef(null);

  console.log(amount);
  useEffect(() => {
    fetchCreditHistory();
  }, []);

  const fetchCreditHistory = () => {
    adminApi.getCurrentCredits().then((res) => {
      setCreditPiceUnit(res.data.creditPiceUnit);
    });
  };

  const startTimer = () => {
    setTimeLeft(600);

    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return timer;
  };

  useEffect(() => {
    if (!show) return;

    setIsScanned(false);
    setIsProcessing(false);

    if (timerRef.current) {
      clearInterval(timerRef.current);
    }

    timerRef.current = startTimer();

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [show]);

  const formatTime = () => {
    const minutes = Math.floor(timeLeft / 60);
    const seconds = timeLeft % 60;
    return `${minutes}:${seconds < 10 ? "0" : ""}${seconds}`;
  };

  const isExpired = timeLeft === 0;

  const handleSendRequestForCredits = async () => {
    try {
      if (!companyName || !mobileNumber) {
        Swal.fire({
          title: "Error!",
          text: "Both Company Name and Mobile Number are required to submit the request.",
          icon: "error",
          confirmButtonText: "Close",
        });
        return;
      }

      const sendingCompanycreditsRequest = {
        userEmail: user?.userEmail,
        userName: user?.userName,
        companyName,
        requestedCredits: parseInt(credits, 10),
        requestCPUnit: parseInt(creditPiceUnit, 5),
        mobileNumber,
        amount: amount,
      };

      // Sending the credit request after confirming the payment
      const response = await adminUserCreditApi.requestUserCredits(
        sendingCompanycreditsRequest,
      );

      setIsProcessing(false);
      Swal.fire({
        title: "Request Submitted!",
        text: "Your request for credits has been sent to the admin. Please wait, the admin will assign credits for you.",
        icon: "success",
        confirmButtonText: "OK",
      });

      console.log("Credit request submitted:", response.data);
    } catch (error) {
      console.error("Error submitting credit request:", error);
      Swal.fire({
        title: "Error!",
        text: "Failed to submit your credit request. Please try again.",
        icon: "error",
        confirmButtonText: "Close",
      });
    }
  };

  return (
    <Modal show={show} onHide={handleClose} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title className="fw-bold d-flex align-items-center">
          Complete Your Payment
          {companyName || franchise ? (
            <h3 className="fw-bold ms-3">{companyName || franchise}</h3>
          ) : null}
          {collaborationName && (
            <h5 className="fw-bold ms-3">for {collaborationName}</h5>
          )}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <div className="row">
          {/* QR Section */}
          <div className="col-md-6 text-center border-end">
            {qrImage ? (
              <img
                src={`data:image/png;base64,${qrImage}`}
                alt="QR Code"
                className="img-fluid p-3"
                style={{ maxWidth: "260px" }}
              />
            ) : (
              <div className="py-5">
                <Spinner animation="border" />
                <p>Generating QR...</p>
              </div>
            )}

            <p className="text-muted mt-3">Scan using any UPI app</p>
            <p>TransactionId: {transactionId}</p>
          </div>

          {/* Payment Info Section */}
          <div className="col-md-6">
            <h5 className="mb-3">Payment Details</h5>

            <div className="mb-2">
              <strong>Fryme Price Unit:</strong>

              <div className="fs-5">
                {price ? (
                  <>Each Fryme price - ₹{price}</>
                ) : (
                  <>Each Fryme price - ₹{creditPiceUnit}</>
                )}
              </div>
            </div>
            <div className="mb-2">
              <strong>Requested Frymes:</strong>
              <div className="fs-5">{credits}</div>
            </div>

            <div className="mb-3">
              <strong>Amount to Pay:</strong>
              <div className="fs-4 text-success fw-bold">₹{amount}</div>
            </div>

            <hr />

            <div className="mb-2">
              <strong>Time Remaining:</strong>
              <div
                className={`fs-4 fw-bold ${
                  isExpired ? "text-danger" : "text-primary"
                }`}
              >
                {formatTime()}
              </div>
            </div>

            {isExpired && (
              <div className="alert alert-danger mt-3">
                QR expired. Please close and submit request again.
              </div>
            )}
          </div>
        </div>
      </Modal.Body>

      <Modal.Footer className="justify-content-between">
        <div className="text-muted small">
          Do not close this window until payment is complete.
        </div>

        {!isScanned && (
          <Button
            variant="primary"
            disabled={isExpired}
            onClick={() => setIsScanned(true)}
          >
            Done Scanned
          </Button>
        )}

        {isScanned && (
          <Button
            variant="success"
            disabled={isExpired || isProcessing}
            onClick={async () => {
              try {
                setIsProcessing(true);

                // 1️⃣ Confirm payment
                await paymentApi.confirmPayment(transactionId);

                // 2️⃣ Only call the credit request if companyName and mobileNumber exist
                await handleSendRequestForCredits();

                // 3️⃣ Show success Swal
                await Swal.fire({
                  title: "Payment Successful!",
                  text: "Admin will verify your payment within 24 hours.",
                  icon: "success",
                  confirmButtonText: "OK",
                });

                // 4️⃣ Close modal after Swal closes
                handleClose(true);
              } catch (error) {
                Swal.fire({
                  title: "Error",
                  text: "Payment expired or request failed.",
                  icon: "error",
                });
              } finally {
                setIsProcessing(false);
              }
            }}
          >
            {isProcessing ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  className="me-2"
                />
                Processing...
              </>
            ) : (
              "I Have Completed Payment"
            )}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default QRCodeModal;
